/* eslint-disable camelcase */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  ModalInitialQuestions, TopBanner, ModalPlanHireSuccess,
} from '../components';
import { useDispatch, useSelector } from 'react-redux';
import { studioActions } from '@/redux/modules/studio';
import {
  CONTENT_TABS,
  FORMATS_TO_RENDER_BY_SLUG, ID_PRO_FEATURES, PLANS, SLUGS_ON_FILTERS,
} from '../studio.enum';
import * as Styles from './StudioHandler.styles';
import ModalFeaturePro from '../components/ModalFeaturePro/ModalFeaturePro';
import HighlightFeatures from '../components/HighlightFeatures';
import { preChatActions } from '@/redux/modules';
import ProjectsBar from '../components/ProjectsBar/ProjectsBar';
import withErrorBoundary from '@/utils/errorBoundary';
import { useStudio } from '@/hooks/studio';
import LoaderPage from '@/pages/Loader/LoaderPage';
import TemplatesList from '../components/TemplatesList';
import { useHistory } from 'react-router';
import useLocale from '@/hooks/useLocale';
import { studioAnalytics } from '@/analytics/studio';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import * as featureFlags from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ModalBlockedPopup } from '@/hooks/useBlockedWindowModal/components/ModalBlockedPopup';
import { createPortal } from 'react-dom';

const StudioHandler = () => {
  const dispatch = useDispatch();
  const {
    credits,
    product,
    templates,
    templatesLoading,
    products: studioProducts,
    accountLoading,
    formCheck,
    documentCreationLoading,
    forMe: templatesForMe,
  } = useSelector(state => state.studio);

  const [loadingProgress, setLoadingProgress] = useState(10);
  const [formAnswered, setFormAnswered] = useState(true);
  const [modalFeatureProToShow, setModalFeatureProToShow] = useState(undefined);
  const [showModalHireSuccess, setShowModalHireSuccess] = useState(false);
  const [seeMoreTemplates, setSeeMoreTemplates] = useState(false);
  const [newTemplatesStart, setNewTemplatesStart] = useState(0);
  const [noMoreTemplates, setNoMoreTemplates] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [pickedTab, setPickedTab] = useState(CONTENT_TABS.FOR_YOU);
  const [canMadeTemplateRequest, setCanMadeTemplateRequest] = useState(true);
  const [displayPopupBlockedModal, setDisplayPopupBlockedModal] = useState(false);

  const { routes: routesLocale } = useLocale();
  const {
    requestUserAccountData,
    requestTemplates,
    provisioningTrailWithoutCreditCard,
    removePopupBlockedModalFromCookie,
    popupBlocked,
  } = useStudio();
  const history = useHistory();
  const { location, replace } = history;

  const isProPlan = product && product.plan_type && product.plan_type !== PLANS.FREE;
  const enableInitialQuestions = useFeatureIsOn(featureFlags.ENABLE_INITIAL_QUESTIONS);

  const slugsListFiltered = useMemo(() => {
    const list = [];

    if (pickedTab === CONTENT_TABS.FOR_YOU) {
      return templatesForMe.list; // remover reverse
    }

    if (!selectedCategories.length) {
      return FORMATS_TO_RENDER_BY_SLUG;
    }

    selectedCategories.forEach((category) => {
      list.push(...SLUGS_ON_FILTERS[category]);
    });

    const sortedList = [...list].sort(
      (a, b) => FORMATS_TO_RENDER_BY_SLUG.indexOf(a) - FORMATS_TO_RENDER_BY_SLUG.indexOf(b),
    );

    return sortedList;
  }, [selectedCategories, pickedTab, templatesForMe.list]);

  const handlePlanHireSuccess = useCallback(() => {
    const { state } = location;
    const redirectedFrom = state && state.redirectFrom;

    if (redirectedFrom === routesLocale.fastcheckout) {
      setShowModalHireSuccess(true);
      replace(location.pathname, {});
    }
  }, [location, replace, routesLocale]);

  useEffect(() => {
    handlePlanHireSuccess();
  }, [handlePlanHireSuccess]);

  const requestDefaultData = useCallback(() => {
    dispatch(preChatActions.productByStatus.request({ withCancelled: true, withPending: true }));
    dispatch(studioActions.formCheck.request());
    dispatch(studioActions.forMe.request());
    dispatch(studioActions.accessTracking.request());
  }, [dispatch]);

  useEffect(() => {
    requestDefaultData();
  }, [requestDefaultData]);

  const hidePopupBlocked = useCallback(() => {
    setDisplayPopupBlockedModal(false);
    removePopupBlockedModalFromCookie();
  }, [removePopupBlockedModalFromCookie]);

  const showPopupBlocked = useCallback(() => {
    if (popupBlocked) {
      setDisplayPopupBlockedModal(true);
    }
  }, [popupBlocked]);

  useEffect(() => {
    showPopupBlocked();
  }, [showPopupBlocked]);

  const getFormAnswered = useCallback(() => {
    formCheck.answered !== undefined && setFormAnswered(formCheck.answered);
  }, [formCheck]);

  const getStudioPrices = useCallback(() => {
    if (studioProducts && !studioProducts.data) {
      dispatch(studioActions.products.request());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, studioProducts.data]);

  useEffect(() => {
    getStudioPrices();
  }, [getStudioPrices]);

  useEffect(() => {
    getFormAnswered();
  }, [getFormAnswered]);

  useEffect(() => {
    requestUserAccountData();
  }, [requestUserAccountData]);

  useEffect(() => {
    if (canMadeTemplateRequest) {
      setSeeMoreTemplates(false);
      setNewTemplatesStart(4);
      requestTemplates({ slugsList: slugsListFiltered.slice(0, 4) });
    }
  }, [requestTemplates, canMadeTemplateRequest, slugsListFiltered]);

  const requestMoreTemplates = useCallback(() => {
    const newTemplatesEnd = newTemplatesStart + 4;

    if (seeMoreTemplates) {
      requestTemplates({ slugsList: slugsListFiltered.slice(newTemplatesStart, newTemplatesEnd) });
      setNewTemplatesStart(newTemplatesEnd);
      setSeeMoreTemplates(false);
    }

    if (newTemplatesStart > slugsListFiltered.length) {
      setNoMoreTemplates(true);
    }
  }, [requestTemplates, seeMoreTemplates, newTemplatesStart, slugsListFiltered]);

  useEffect(() => {
    requestMoreTemplates();
  }, [requestMoreTemplates]);

  const cleanUpFunction = useCallback(() => {
    dispatch(studioActions.templates.reset());
  }, [dispatch]);

  useEffect(() => () => {
    cleanUpFunction();
  }, [cleanUpFunction]);

  const handleShowModalGetPro = useCallback(() => {
    studioAnalytics.bannerProClick();
    setModalFeatureProToShow(product && product.trial_available ? ID_PRO_FEATURES.STUDIO_PRO_OFFER : ID_PRO_FEATURES.STUDIO_PRO);
  }, [setModalFeatureProToShow, product]);

  const defineProductPrice = useCallback(() => {
    if (!studioProducts || !studioProducts.data || !studioProducts.data.studio_pro || !studioProducts.data.studio_discount) return '';

    const { data } = studioProducts;
    const { has_discount, studio_pro, studio_discount } = data;

    if (has_discount) {
      return formatCurrency(studio_discount.price);
    }

    return formatCurrency(studio_pro.price);
  }, [studioProducts]);

  const handleSetPickedTab = useCallback((option) => {
    dispatch(studioActions.templates.reset());

    setCanMadeTemplateRequest(false);
    setNoMoreTemplates(false);
    setSeeMoreTemplates(false);
    setNewTemplatesStart(4);

    setPickedTab(option);
  }, [dispatch]);

  const topBannerProps = useCallback(() => ({
    isProPlan,
    product,
    credits,
    trialRemainingDays: product && product.plan_type === PLANS.TRIAL && product.trial_remaining_days,
    handleShowModalGetPro,
    paidWithCreditCard: product && product.payment_card,
    planPrice: defineProductPrice(),
    loading: accountLoading,
    pickedTab,
    handleSetPickedTab,
    disableForMeOption: templatesForMe.loading || templatesForMe.status === 'failure',
  }), [credits, isProPlan, product, accountLoading, pickedTab, templatesForMe.loading, templatesForMe.status, handleShowModalGetPro, defineProductPrice, handleSetPickedTab]);

  const highlightFeaturesProps = useCallback(() => ({
    isProPlan,
    credits: credits && credits.available,
    setModalFeatureProToShow,
  }), [credits, isProPlan]);

  const allowMadeTemplateRequest = useCallback(() => {
    if (!canMadeTemplateRequest && selectedCategories) {
      setTimeout(() => {
        setCanMadeTemplateRequest(true);
      }, 2000);
    }
  }, [canMadeTemplateRequest, selectedCategories]);

  useEffect(() => {
    allowMadeTemplateRequest();
  }, [allowMadeTemplateRequest]);

  const templatesListProps = useCallback(() => {
    const handleSetSelectedCategories = (categoriesList) => {
      dispatch(studioActions.templates.reset());

      setSelectedCategories(categoriesList);

      setCanMadeTemplateRequest(false);
      setNoMoreTemplates(false);
      setSeeMoreTemplates(false);
      setNewTemplatesStart(4);
    };

    return {
      isProPlan,
      templates,
      templatesLoading,
      setModalFeatureProToShow,
      handleShowModalGetPro,
      setSeeMoreTemplates,
      noMoreTemplates,
      slugsListFiltered,
      selectedCategories,
      canMadeTemplateRequest,
      setSelectedCategories: handleSetSelectedCategories,
      displayFilterCategories: pickedTab === CONTENT_TABS.DESIGN,
    };
  }, [
    dispatch,
    isProPlan,
    templates,
    templatesLoading,
    handleShowModalGetPro,
    noMoreTemplates,
    selectedCategories,
    slugsListFiltered,
    canMadeTemplateRequest,
    pickedTab,
  ]);

  const modalInitialQuestionsProps = useCallback(() => {
    const handleSubmit = (dataToSend) => {
      dispatch(studioActions.formSend.request(dataToSend));
    };

    return ({
      isProPlan,
      plan: product && product.plan_type,
      handleSubmit,
      setFormHasAnswered: setFormAnswered,
    });
  }, [dispatch, isProPlan, product]);

  const modalFeatureProProps = useCallback(() => {
    const productsData = studioProducts && studioProducts.data;

    const purchaseTrialWithoutCc = () => {
      const onSuccessCallback = () => {
        setModalFeatureProToShow(undefined);
        history.push(routesLocale.studio, { redirectFrom: routesLocale.fastcheckout });
      };

      provisioningTrailWithoutCreditCard(onSuccessCallback);
    };

    return {
      isProPlan,
      trialAvailable: product && product.trial_available,
      productTrial: productsData && productsData.studio_trial,
      productPro: productsData && productsData.studio_pro,
      discount: productsData && productsData.studio_discount,
      hasDiscount: productsData && productsData.has_discount,
      accountProduct: product,
      setModalFeatureProToShow,
      modalId: modalFeatureProToShow,
      renewalDate: credits && credits.renewal_date,
      planPrice: defineProductPrice(),
      purchaseTrialWithoutCc,
    };
  }, [studioProducts, credits, modalFeatureProToShow, isProPlan, product, defineProductPrice, history, provisioningTrailWithoutCreditCard, routesLocale]);

  const loadingProgressFunc = useCallback(() => {
    const interval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 85) {
          clearInterval(interval);
        }
        return prev + 5;
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadingProgressFunc();
  }, [loadingProgressFunc]);

  if (!(product && product.plan_type) || documentCreationLoading) {
    return createPortal(
      <Styles.WrapperLoaderPage>
        <LoaderPage progress={loadingProgress} />
      </Styles.WrapperLoaderPage>,
      document.body,
    );
  }

  const projectsBarProps = () => ({
    isProPlan,
    credits: credits && credits.available,
    disableForMeOption: templatesForMe.loading || templatesForMe.status === 'failure',
    pickedTab,
    handleSetPickedTab,
  });

  const modalPlanHideSuccessProps = () => ({
    closeModal: () => setShowModalHireSuccess(false),
  });

  return (
    <>
      <Styles.Container>
        <TopBanner {...topBannerProps()} />
        <HighlightFeatures {...highlightFeaturesProps()} />
        <ProjectsBar {...projectsBarProps()} />
        <TemplatesList {...templatesListProps()} />
      </Styles.Container>

      {!formAnswered && enableInitialQuestions && <ModalInitialQuestions {...modalInitialQuestionsProps()} />}
      {modalFeatureProToShow && <ModalFeaturePro {...modalFeatureProProps()} />}
      {showModalHireSuccess && <ModalPlanHireSuccess {...modalPlanHideSuccessProps()} />}
      {displayPopupBlockedModal && <ModalBlockedPopup onClose={hidePopupBlocked} />}
    </>
  );
};

export default withErrorBoundary(StudioHandler, 'StudioHandler');
